import React, { useState, useEffect } from "react";
import { UserProfile, UserAccountType } from "../../types/userProfile";
import { User } from "firebase/auth";
import { useAuth } from "../../contexts";
import { 
  loginWithGoogle, 
  saveUserProfileFirestore 
} from "../../utils/firebase";
import { 
  isProfileIncomplete, 
  saveUserProfile, 
  findExistingUserProfile,
  DEFAULT_USER_PROFILE 
} from "../../utils/userProfileStorage";
import CityAutocomplete from "../common/CityAutocomplete";
import { 
  User as UserIcon, 
  Building2, 
  X, 
  LogIn, 
  UserPlus, 
  AlertTriangle, 
  Info,
  ShieldCheck,
  ArrowLeft
} from "lucide-react";
import { motion } from "motion/react";

interface GoogleAuthModalProps {
  user: User | null;
  userProfile: UserProfile;
  isOpen: boolean;
  onClose: () => void;
  onUpdateProfile: (updated: UserProfile) => void;
  onToast: (msg: string, type?: "success" | "error") => void;
  onRedirectToProfile?: () => void;
  onLoginSuccess?: (profile: UserProfile) => void;
}

/**
 * Aplica máscara automática para CPF (11 dígitos) ou CNPJ (14 dígitos)
 */
export function formatCpfCnpj(value: string): string {
  const digits = value.replace(/\D/g, "").slice(0, 14);
  if (digits.length <= 11) {
    return digits
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
  } else {
    return digits
      .replace(/^(\d{2})(\d)/, "$1.$2")
      .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
      .replace(/\.(\d{3})(\d)/, ".$1/$2")
      .replace(/(\d{4})(\d{1,2})$/, "$1-$2");
  }
}

export default function GoogleAuthModal({
  user,
  userProfile,
  isOpen,
  onClose,
  onUpdateProfile,
  onToast,
  onRedirectToProfile,
  onLoginSuccess,
}: GoogleAuthModalProps) {
  const { authModalRole } = useAuth();

  // Estados de fluxo estrito: "initial" | "login" | "register_role" | "register_form"
  const [step, setStep] = useState<"initial" | "login" | "register_role" | "register_form">("initial");
  const [selectedRole, setSelectedRole] = useState<UserAccountType>("atleta");
  const [isLoading, setIsLoading] = useState(false);
  const [notice, setNotice] = useState<{ msg: string; type: "warning" | "info" | "error" | "success" } | null>(null);

  // Sincroniza estado de entrada contextual quando o modal abre
  useEffect(() => {
    if (isOpen) {
      if (authModalRole === "dono_arena") {
        setSelectedRole("dono_arena");
        setStep("register_form");
      } else {
        setSelectedRole("atleta");
        setStep("login");
      }
    }
  }, [isOpen, authModalRole]);

  // Campos de cadastro complementar
  const [regName, setRegName] = useState("");
  const [regPhone, setRegPhone] = useState("");
  const [regCpf, setRegCpf] = useState("");
  const [regArenaName, setRegArenaName] = useState("");
  const [regCity, setRegCity] = useState("São Paulo");
  const [regState, setRegState] = useState("SP");

  if (!isOpen) return null;

  // 1. FLUXO DE LOGIN COM GOOGLE
  const handleGoogleLoginAction = async () => {
    setIsLoading(true);
    setNotice(null);

    try {
      const googleUser = await loginWithGoogle();

      if (!googleUser) {
        setIsLoading(false);
        return;
      }

      const userEmail = googleUser.email || "";
      const userUid = googleUser.uid;

      // Consulta perfil existente autoritativo
      let existingProfile = await findExistingUserProfile(userUid);
      if (!existingProfile && userEmail) {
        existingProfile = await findExistingUserProfile(userEmail);
      }

      if (existingProfile && existingProfile.id !== "guest_visitor") {
        const restoredProfile: UserProfile = {
          ...DEFAULT_USER_PROFILE,
          ...existingProfile,
          id: userUid,
          email: userEmail || existingProfile.email || "",
          avatarUrl: googleUser.photoURL || existingProfile.avatarUrl || "",
        };

        saveUserProfile(restoredProfile);
        await saveUserProfileFirestore(restoredProfile);
        onUpdateProfile(restoredProfile);

        onToast(`🎉 Bem-vindo(a) de volta, ${restoredProfile.name || "Usuário"}!`, "success");
        onClose();
        if (onLoginSuccess) {
          onLoginSuccess(restoredProfile);
        } else if (isProfileIncomplete(restoredProfile) && onRedirectToProfile) {
          onRedirectToProfile();
        }
      } else {
        // Novo usuário entrando com Google: cria perfil inicial determinístico de Atleta
        const newProfile: UserProfile = {
          ...DEFAULT_USER_PROFILE,
          id: userUid,
          name: googleUser.displayName || "Atleta",
          email: userEmail,
          accountType: "atleta",
          role: "atleta",
          avatarUrl: googleUser.photoURL || "",
          city: "",
          state: "",
          phone: "",
          createdAt: Date.now(),
        };

        saveUserProfile(newProfile);
        await saveUserProfileFirestore(newProfile);
        onUpdateProfile(newProfile);

        onToast(`🎉 Bem-vindo(a) ao Hub Esportivo, ${newProfile.name}! Complete seus dados no perfil.`, "success");
        onClose();
        if (onLoginSuccess) {
          onLoginSuccess(newProfile);
        } else if (onRedirectToProfile) {
          onRedirectToProfile();
        }
      }
    } catch (err: any) {
      console.error("Erro no login Google:", err);
      if (err?.code === "auth/popup-blocked") {
        onToast("Pop-up bloqueado pelo navegador. Permita os pop-ups para entrar com o Google.", "error");
      } else {
        onToast("Falha na autenticação com o Google. Tente novamente.", "error");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // 2. FLUXO DE CADASTRO COM GOOGLE (APÓS ESCOLHA DE PAPEL)
  const handleGoogleRegisterAction = async () => {
    setIsLoading(true);
    setNotice(null);

    try {
      const googleUser = await loginWithGoogle();

      if (!googleUser) {
        setIsLoading(false);
        return;
      }

      const userEmail = googleUser.email || "";
      const userUid = googleUser.uid;
      const googleName = googleUser.displayName || "";

      // Verifica se já existe perfil para este usuário
      let existingProfile = await findExistingUserProfile(userUid);
      if (!existingProfile && userEmail) {
        existingProfile = await findExistingUserProfile(userEmail);
      }

      if (existingProfile && existingProfile.id !== "guest_visitor") {
        // Se já possui conta, atualiza/vincula mantendo os dados e ajustando o papel se necessário
        const cleanDoc = regCpf.replace(/\D/g, "");
        const isCnpjDoc = cleanDoc.length > 11;

        const updatedProfile: UserProfile = {
          ...DEFAULT_USER_PROFILE,
          ...existingProfile,
          id: userUid,
          email: userEmail || existingProfile.email || "",
          avatarUrl: googleUser.photoURL || existingProfile.avatarUrl || "",
          accountType: selectedRole, // Atualiza para o papel escolhido no cadastro atual
          cpf: isCnpjDoc ? (existingProfile.cpf || "") : (regCpf.trim() || existingProfile.cpf || ""),
          cnpj: isCnpjDoc ? regCpf.trim() : (existingProfile.cnpj || ""),
          arenaName: selectedRole === "dono_arena" ? (regArenaName.trim() || existingProfile.arenaName || "Minha Arena") : existingProfile.arenaName,
          arenaVerification: selectedRole === "dono_arena" && !existingProfile.arenaVerification?.status ? { status: "pendente" } : existingProfile.arenaVerification,
          updatedAt: Date.now(),
        };

        saveUserProfile(updatedProfile);
        await saveUserProfileFirestore(updatedProfile);
        onUpdateProfile(updatedProfile);

        onToast(`✅ Conta atualizada com sucesso para ${selectedRole === "dono_arena" ? "Dono de Arena" : "Atleta"}!`, "success");
        onClose();
        if (onLoginSuccess) {
          onLoginSuccess(updatedProfile);
        }
      } else {
        // Criação de novo perfil estrito com o papel escolhido
        const cleanDoc = regCpf.replace(/\D/g, "");
        const isCnpjDoc = cleanDoc.length > 11;

        const newProfile: UserProfile = {
          ...DEFAULT_USER_PROFILE,
          id: userUid,
          name: regName.trim() || googleName || (selectedRole === "dono_arena" ? "Gestor de Arena" : "Atleta"),
          email: userEmail,
          accountType: selectedRole,
          avatarUrl: googleUser.photoURL || "",
          phone: regPhone.trim(),
          cpf: isCnpjDoc ? "" : regCpf.trim(),
          cnpj: isCnpjDoc ? regCpf.trim() : "",
          city: regCity.trim() || "São Paulo",
          state: regState.trim() || "SP",
          arenaName: selectedRole === "dono_arena" ? (regArenaName.trim() || "Minha Arena") : "",
          arenaVerification: selectedRole === "dono_arena" ? { status: "pendente" } : undefined,
          createdAt: Date.now(),
        };

        saveUserProfile(newProfile);
        await saveUserProfileFirestore(newProfile);
        onUpdateProfile(newProfile);

        onToast(`✅ Conta de ${selectedRole === "dono_arena" ? "Dono de Arena (Pendente de Aprovação)" : "Atleta"} criada com sucesso!`, "success");
        onClose();
        if (onLoginSuccess) {
          onLoginSuccess(newProfile);
        } else if (isProfileIncomplete(newProfile) && onRedirectToProfile) {
          onRedirectToProfile();
        }
      }
    } catch (err: any) {
      console.error("Erro no cadastro Google:", err);
      if (err?.code === "auth/popup-blocked") {
        onToast("Pop-up bloqueado pelo navegador. Permita os pop-ups para continuar.", "error");
      } else {
        onToast("Falha na autenticação com o Google. Tente novamente.", "error");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 dark:bg-black/85 backdrop-blur-md z-50 flex items-center justify-center p-4 overflow-y-auto">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95, y: 15 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 15 }}
        className="bg-white dark:bg-zinc-950 border border-slate-200 dark:border-zinc-800 rounded-3xl p-6 sm:p-8 max-w-md w-full text-slate-900 dark:text-white shadow-2xl space-y-6 relative my-8"
      >
        {/* BOTÃO FECHAR */}
        <button
          onClick={onClose}
          className="absolute top-5 right-5 p-2 rounded-full bg-slate-100 hover:bg-slate-200 dark:bg-zinc-900 text-slate-500 hover:text-slate-900 dark:text-zinc-400 dark:hover:text-white transition cursor-pointer"
        >
          <X size={18} />
        </button>

        {/* BOTÃO VOLTAR OU NAVEGAÇÃO PARA PÁGINA INICIAL */}
        {step !== "initial" && step !== "login" ? (
          <button
            onClick={() => {
              if (step === "register_form" && authModalRole === "dono_arena") {
                onClose();
              } else if (step === "register_form") {
                setStep("register_role");
              } else {
                setStep("login");
              }
              setNotice(null);
            }}
            className="absolute top-5 left-5 p-2 rounded-full bg-slate-100 hover:bg-slate-200 dark:bg-zinc-900 text-slate-500 hover:text-slate-900 dark:text-zinc-400 dark:hover:text-white transition flex items-center gap-1 text-xs cursor-pointer"
          >
            <ArrowLeft size={16} />
            <span>Voltar</span>
          </button>
        ) : (
          !user && (
            <button
              onClick={() => {
                onClose();
                window.history.pushState({}, "", "/");
                window.dispatchEvent(new Event("popstate"));
              }}
              className="absolute top-5 left-5 px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 dark:bg-zinc-900 text-slate-600 hover:text-slate-900 dark:text-zinc-400 dark:hover:text-white transition flex items-center gap-1.5 text-xs font-semibold cursor-pointer"
              title="Ir para a Página Inicial da plataforma"
            >
              <ArrowLeft size={15} />
              <span>Página Inicial</span>
            </button>
          )
        )}

        {/* TELA INICIAL: ESCOLHA ENTRE ENTRAR E CADASTRAR */}
        {step === "initial" && (
          <div className="text-center space-y-5 pt-2">
            <div className="w-16 h-16 bg-gradient-to-tr from-amber-400 to-emerald-400 text-zinc-950 rounded-2xl flex items-center justify-center mx-auto shadow-xl">
              <ShieldCheck size={32} />
            </div>
            
            <div className="space-y-2">
              <h2 className="text-2xl font-display font-black text-slate-900 dark:text-white">Hub Esportivo</h2>
              <p className="text-xs text-slate-600 dark:text-zinc-400 max-w-xs mx-auto">
                Acesse sua conta para gerenciar reservas, quadras e torneios de forma 100% segura com Google.
              </p>
            </div>

            {notice && (
              <div className="p-3 rounded-2xl bg-amber-50 dark:bg-amber-500/15 border border-amber-300 dark:border-amber-400/80 text-amber-800 dark:text-amber-200 text-xs text-left flex items-start gap-2.5">
                <AlertTriangle size={16} className="shrink-0 mt-0.5 text-amber-600 dark:text-amber-400" />
                <span>{notice.msg}</span>
              </div>
            )}

            <div className="space-y-3 pt-2">
              <button
                onClick={() => setStep("login")}
                className="w-full py-3.5 px-4 bg-emerald-500 hover:bg-emerald-400 text-white dark:text-zinc-950 font-black text-xs rounded-2xl shadow-lg transition flex items-center justify-center gap-2 cursor-pointer"
              >
                <LogIn size={16} />
                <span>ENTRAR</span>
              </button>

              <button
                onClick={() => setStep("register_role")}
                className="w-full py-3.5 px-4 bg-slate-100 hover:bg-slate-200 dark:bg-zinc-900 dark:hover:bg-zinc-800 border border-slate-300 dark:border-zinc-700 text-slate-800 dark:text-white font-black text-xs rounded-2xl shadow-lg transition flex items-center justify-center gap-2 cursor-pointer"
              >
                <UserPlus size={16} className="text-amber-500 dark:text-amber-400" />
                <span>CADASTRAR</span>
              </button>
            </div>
          </div>
        )}

        {/* TELA DE LOGIN COM GOOGLE */}
        {step === "login" && (
          <div className="text-center space-y-5 pt-2">
            <div className="w-14 h-14 bg-emerald-50 dark:bg-emerald-500/20 border border-emerald-200 dark:border-emerald-500/40 text-emerald-600 dark:text-emerald-400 rounded-2xl flex items-center justify-center mx-auto">
              <LogIn size={26} />
            </div>

            <div className="space-y-2">
              <h2 className="text-xl font-display font-black text-slate-900 dark:text-white">Acessar Conta</h2>
              <p className="text-xs text-slate-600 dark:text-zinc-400 max-w-xs mx-auto">
                Clique abaixo para autenticar com sua conta Google. O sistema identificará automaticamente seu perfil (Atleta, Dono de Arena ou Super Admin).
              </p>
            </div>

            <button
              type="button"
              onClick={handleGoogleLoginAction}
              disabled={isLoading}
              className="w-full py-3.5 px-4 bg-white hover:bg-slate-50 dark:bg-white dark:hover:bg-zinc-100 text-slate-900 dark:text-zinc-950 border border-slate-300 dark:border-transparent font-black text-xs rounded-2xl shadow-xl transition flex items-center justify-center gap-3 cursor-pointer"
            >
              <svg className="w-5 h-5 shrink-0" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" />
              </svg>
              <span>{isLoading ? "Autenticando..." : "CONTINUAR COM GOOGLE"}</span>
            </button>
          </div>
        )}

        {/* TELA DE CADASTRO: ESCOLHA DE PAPEL (ATLETA vs DONO DE ARENA) */}
        {step === "register_role" && (
          <div className="space-y-5 pt-2">
            <div className="text-center space-y-2">
              <h2 className="text-xl font-display font-black text-slate-900 dark:text-white">Como você deseja utilizar?</h2>
              <p className="text-xs text-slate-600 dark:text-zinc-400">
                Selecione o seu perfil principal antes de prosseguir com o Google.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-3">
              <button
                type="button"
                onClick={() => setSelectedRole("atleta")}
                className={`p-4 rounded-2xl border transition text-left flex items-center gap-3.5 cursor-pointer ${
                  selectedRole === "atleta"
                    ? "bg-emerald-50 dark:bg-emerald-950/80 border-emerald-500 text-slate-900 dark:text-white shadow-lg"
                    : "bg-slate-50 dark:bg-zinc-900/60 border-slate-200 dark:border-zinc-800 text-slate-600 dark:text-zinc-400 hover:border-slate-300 dark:hover:border-zinc-700"
                }`}
              >
                <div className={`p-2.5 rounded-xl shrink-0 ${selectedRole === "atleta" ? "bg-emerald-500 text-white dark:text-zinc-950" : "bg-slate-200 dark:bg-zinc-800 text-slate-600 dark:text-zinc-400"}`}>
                  <UserIcon size={20} />
                </div>
                <div>
                  <span className="block font-black text-sm text-slate-900 dark:text-white">Atleta</span>
                  <span className="text-xs text-slate-600 dark:text-zinc-400">Jogar partidas, agendar quadras e participar de torneios.</span>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setSelectedRole("dono_arena")}
                className={`p-4 rounded-2xl border transition text-left flex items-center gap-3.5 cursor-pointer ${
                  selectedRole === "dono_arena"
                    ? "bg-amber-50 dark:bg-amber-950/80 border-amber-500 dark:border-amber-400 text-slate-900 dark:text-white shadow-lg"
                    : "bg-slate-50 dark:bg-zinc-900/60 border-slate-200 dark:border-zinc-800 text-slate-600 dark:text-zinc-400 hover:border-slate-300 dark:hover:border-zinc-700"
                }`}
              >
                <div className={`p-2.5 rounded-xl shrink-0 ${selectedRole === "dono_arena" ? "bg-amber-500 dark:bg-amber-400 text-white dark:text-zinc-950" : "bg-slate-200 dark:bg-zinc-800 text-slate-600 dark:text-zinc-400"}`}>
                  <Building2 size={20} />
                </div>
                <div>
                  <span className="block font-black text-sm text-slate-900 dark:text-white">Dono de Arena</span>
                  <span className="text-xs text-slate-600 dark:text-zinc-400">Cadastrar quadras, gerenciar reservas e recebimentos. (Sujeito a aprovação)</span>
                </div>
              </button>
            </div>

            <button
              onClick={() => setStep("register_form")}
              className="w-full py-3.5 bg-gradient-to-r from-amber-400 to-emerald-400 text-zinc-950 font-black text-xs rounded-2xl shadow-xl transition flex items-center justify-center gap-2 cursor-pointer mt-2"
            >
              <span>Avançar com Perfil de {selectedRole === "dono_arena" ? "Dono de Arena" : "Atleta"}</span>
            </button>
          </div>
        )}

        {/* TELA DE CADASTRO: FORMULÁRIO COMPLEMENTAR E GOOGLE */}
        {step === "register_form" && (
          <div className="space-y-4 pt-1">
            <div className="text-center space-y-1">
              <h2 className="text-lg font-display font-black text-slate-900 dark:text-white">
                Cadastro de {selectedRole === "dono_arena" ? "Dono de Arena" : "Atleta"}
              </h2>
              <p className="text-xs text-slate-600 dark:text-zinc-400">
                Preencha seus dados básicos e finalize com o Google.
              </p>
            </div>

            <div className="space-y-3">
              {selectedRole === "dono_arena" && (
                <div>
                  <label className="block text-xs font-bold text-amber-700 dark:text-amber-300 mb-1">
                    Nome da Quadra / Arena: *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="Ex: Arena Praia & Sol"
                    value={regArenaName}
                    onChange={(e) => setRegArenaName(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-zinc-900 border border-slate-300 dark:border-zinc-800 rounded-xl text-xs text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-400"
                  />
                </div>
              )}

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[11px] font-bold text-slate-700 dark:text-zinc-300 mb-1">Nome Completo:</label>
                  <input
                    type="text"
                    placeholder="Seu nome"
                    value={regName}
                    onChange={(e) => setRegName(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-zinc-900 border border-slate-300 dark:border-zinc-800 rounded-xl text-xs text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>

                <div>
                  <label className="block text-[11px] font-bold text-slate-700 dark:text-zinc-300 mb-1">WhatsApp:</label>
                  <input
                    type="text"
                    placeholder="(00) 90000-0000"
                    value={regPhone}
                    onChange={(e) => setRegPhone(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-zinc-900 border border-slate-300 dark:border-zinc-800 rounded-xl text-xs text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-bold text-slate-700 dark:text-zinc-300 mb-1">
                  CPF ou CNPJ:
                </label>
                <input
                  type="text"
                  placeholder="000.000.000-00 ou 00.000.000/0000-00"
                  value={regCpf}
                  onChange={(e) => setRegCpf(formatCpfCnpj(e.target.value))}
                  maxLength={18}
                  className="w-full px-3 py-2 bg-slate-50 dark:bg-zinc-900 border border-slate-300 dark:border-zinc-800 rounded-xl text-xs text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div>
                <CityAutocomplete
                  stateValue={regState}
                  onStateChange={setRegState}
                  cityValue={regCity}
                  onCityChange={setRegCity}
                  darkTheme={false}
                />
              </div>
            </div>

            <button
              type="button"
              onClick={handleGoogleRegisterAction}
              disabled={isLoading}
              className="w-full py-3.5 px-4 bg-gradient-to-r from-amber-400 to-emerald-400 hover:from-amber-300 hover:to-emerald-300 text-zinc-950 font-black text-xs rounded-2xl shadow-xl transition flex items-center justify-center gap-3 cursor-pointer mt-2"
            >
              <svg className="w-5 h-5 shrink-0" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" />
              </svg>
              <span>{isLoading ? "Criando conta..." : `CONCLUIR COM GOOGLE (${selectedRole === "dono_arena" ? "Arena" : "Atleta"})`}</span>
            </button>
          </div>
        )}

        {/* STATUS DA SESSÃO / CONTEXTO DE CADASTRO */}
        <div className="border-t border-slate-200 dark:border-zinc-900 pt-3 flex items-center justify-between text-xs text-slate-500 dark:text-zinc-400">
          {user ? (
            <>
              <span>Conta Ativa: <strong className="text-slate-800 dark:text-white">{userProfile.name}</strong></span>
              <span className="bg-slate-100 dark:bg-zinc-900 px-2.5 py-1 rounded-lg text-[10px] text-amber-600 dark:text-amber-400 font-bold uppercase">
                {userProfile.accountType === "dono_arena" ? "🏟️ Dono de Arena" : "🏃‍♂️ Atleta"}
              </span>
            </>
          ) : (
            <>
              <span>Cadastro: <strong className="text-slate-800 dark:text-white">{selectedRole === "dono_arena" ? "Dono de Arena" : "Atleta"}</strong></span>
              <span className="bg-slate-100 dark:bg-zinc-900 px-2.5 py-1 rounded-lg text-[10px] text-amber-600 dark:text-amber-400 font-bold uppercase">
                {selectedRole === "dono_arena" ? "🏟️ Dono de Arena" : "🏃‍♂️ Atleta"}
              </span>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}
